import type {OperatorFunction, Observable, BehaviorSubject} from 'rxjs';
import {bufferWhen, debounceTime, filter, share, mergeWith} from 'rxjs/operators';
import {ReplaySubject, Subject} from 'rxjs';
import {isNotEmpty} from 'shared/utils/array-utils';

export function shareWithRefCountAndReplayLast() {
    return <T>(source: Observable<T>): Observable<T> => {
        return source.pipe(
            share({
                connector: () => new ReplaySubject(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: true
            })
        );
    };
}

export function shareWithRefCount() {
    return <T>(source: Observable<T>): Observable<T> => {
        return source.pipe(
            share({
                connector: () => new Subject(),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: true
            })
        );
    };
}

/**
 * Custom RxJS operator:
 * Buffers values from the source Observable and emits the buffer when either:
 * - A period of inactivity (`debounceMs`) occurs (no new values emitted within the time window), or
 * - A signal is received from the provided `flushSignal` Observable.
 *
 * Each buffer is dynamically managed and will reset upon emission.
 *
 * Empty buffers are filtered out, so only non-empty arrays are emitted.
 *
 * @template T The type of values emitted by the source Observable.
 * @param {number} debounceMs - The debounce time (in milliseconds) used to determine inactivity.
 * @param {Observable<any>} flushSignal - An external Observable that triggers immediate buffer flush.
 * @returns {OperatorFunction<T, T[]>} An RxJS operator function that buffers values and emits them based on debounce time or flush signal.
 */
export function bufferWithDebounceTimeAndSignalFlushing<T>(debounceMs: number, flushSignal: Observable<any>): OperatorFunction<T, T[]> {
    return (source: Observable<T>): Observable<T[]> => {
        const sharedSource = source.pipe(shareWithRefCount());
        return sharedSource.pipe(
            bufferWhen(() => sharedSource.pipe(debounceTime(debounceMs), mergeWith(flushSignal))),
            filter(isNotEmpty) // filter empty buffer flushes
        );
    };
}


export function updateValue<T>(behaviorSubject: BehaviorSubject<T>, updater: (value: T) => T) {
    const value = behaviorSubject.getValue();
    behaviorSubject.next(updater(value));
}